import React from 'react';
import { Button } from '../components/ui/button';
import { ShoppingCart, Bell, Search, BarChart3, Shield, Zap } from 'lucide-react';

const DealerPage = () => {
  const benefits = [
    {
      icon: ShoppingCart,
      title: 'Tilgang til tusenvis av biler',
      description: 'Få tilgang til biler fra hele Norge gjennom vår plattform'
    },
    {
      icon: Bell,
      title: 'Sanntidsvarsler',
      description: 'Få varsler når biler som matcher dine kriterier legges ut'
    },
    {
      icon: Search,
      title: 'Avansert søk',
      description: 'Finn nøyaktig de bilene du leter etter med våre søkeverktøy'
    },
    {
      icon: BarChart3,
      title: 'Markedsanalyse',
      description: 'Få innsikt i markedspriser og trender'
    },
    {
      icon: Shield,
      title: 'Trygt og sikkert',
      description: 'Verifiserte biler med grundige tilstandsrapporter'
    },
    {
      icon: Zap,
      title: 'Rask prosess',
      description: 'Effektiv budrunde og rask gjennomføring av kjøp'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Forhandlerportal
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Bli med i Norges største nettverk for bilhandel. Over 2000 forhandlere bruker allerede DigiCar for å finne kvalitetsbiler til konkurransedyktige priser.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold text-lg px-8 py-6">
                  Logg inn
                </Button>
                <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-black font-semibold text-lg px-8 py-6">
                  Bli forhandler
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1652660771416-032b5debd5be?w=800&h=600&fit=crop" 
                alt="Bilforhandler" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Hvorfor DigiCar?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Vår plattform gir deg alle verktøyene du trenger for å drive en effektiv bilhandel
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index} 
                  className="bg-gray-50 rounded-2xl p-8 hover:bg-yellow-50 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="w-14 h-14 bg-yellow-500 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-black" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Slik fungerer det
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                step: 1,
                title: 'Registrer deg',
                description: 'Opprett en forhandlerkonto og kom i gang'
              },
              {
                step: 2,
                title: 'Bla gjennom biler',
                description: 'Søk og finn biler som matcher dine behov'
              },
              {
                step: 3,
                title: 'Gi bud',
                description: 'Delta i budrunden og konkurrer om bilene'
              },
              {
                step: 4,
                title: 'Kjøp og hent',
                description: 'Vinner du, avtales henting og betaling'
              }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-yellow-500 text-black rounded-full flex items-center justify-center text-2xl font-bold shadow-lg mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-yellow-600 mb-2">2000+</div>
              <div className="text-xl text-gray-600">Aktive forhandlere</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-yellow-600 mb-2">90,000+</div>
              <div className="text-xl text-gray-600">Biler solgt</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-yellow-600 mb-2">4.7/5</div>
              <div className="text-xl text-gray-600">Forhandlervurdering</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-yellow-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Klar til å bli med?
          </h2>
          <p className="text-xl text-gray-900 mb-8">
            Registrer deg i dag og få tilgang til tusenvis av biler
          </p>
          <Button className="bg-black hover:bg-gray-800 text-white font-semibold text-lg px-8 py-6">
            Bli forhandler nå
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DealerPage;